import { useState, useEffect } from 'react';
import { query, where, onSnapshot, doc, updateDoc, deleteDoc, Timestamp, collectionGroup } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from './useAuth';

/**
 * useAccessRequestListener - Listen for approved access requests waiting for teacher confirmation
 *
 * This hook monitors all schools' accessRequests collections for requests where:
 * - userId matches current user
 * - status is 'approvedPending' (admin approved, waiting for teacher)
 *
 * Returns:
 * - pendingInvitation: Object with school info and assigned role, or null
 * - acceptInvitation: Function to accept the invitation
 * - declineInvitation: Function to decline the invitation
 * - loading: Boolean
 * - error: String or null
 */
export const useAccessRequestListener = () => {
  const { user } = useAuth();
  const [pendingInvitation, setPendingInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }

    // We need to query across all schools' accessRequests subcollections
    // Using collectionGroup to query across all schools
    const q = query(
      collectionGroup(db, 'accessRequests'),
      where('userId', '==', user.uid),
      where('status', '==', 'approvedPending')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          // Get the first pending invitation (should only be one)
          const invitationDoc = snapshot.docs[0];
          const data = invitationDoc.data();

          // Extract schoolId from the document path (accessRequests is a subcollection)
          const pathParts = invitationDoc.ref.path.split('/');
          const schoolId = pathParts[1]; // Format: schools/{schoolId}/accessRequests/{requestId}

          setPendingInvitation({
            id: invitationDoc.id,
            schoolId,
            schoolName: data.schoolName,
            assignedRole: data.assignedRole,
            approvedAt: data.approvedAt,
            ...data
          });
        } else {
          setPendingInvitation(null);
        }
        setLoading(false);
      },
      (err) => {
        console.error('[useAccessRequestListener] Error listening for invitations:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  /**
   * Accept the pending invitation
   * - Updates user document with schoolId, role, and staff status
   * - Deletes the access request document
   */
  const acceptInvitation = async () => {
    if (!pendingInvitation || !user?.uid) {
      throw new Error('No pending invitation to accept');
    }

    try {
      // Update user document
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        schoolId: pendingInvitation.schoolId,
        assignedSchoolId: pendingInvitation.schoolId,
        role: pendingInvitation.assignedRole,
        staff: true,
        updatedAt: Timestamp.now()
      });

      // Delete the access request
      const requestRef = doc(db, 'schools', pendingInvitation.schoolId, 'accessRequests', pendingInvitation.id);
      await deleteDoc(requestRef);

      setPendingInvitation(null);
      return true;
    } catch (err) {
      console.error('[useAccessRequestListener] Error accepting invitation:', err);
      setError(err.message);
      throw err;
    }
  };

  /**
   * Decline the pending invitation
   * - Deletes the access request document
   * - Does NOT update user document
   */
  const declineInvitation = async () => {
    if (!pendingInvitation) {
      throw new Error('No pending invitation to decline');
    }

    try {
      // Delete the access request
      const requestRef = doc(db, 'schools', pendingInvitation.schoolId, 'accessRequests', pendingInvitation.id);
      await deleteDoc(requestRef);

      setPendingInvitation(null);
      return true;
    } catch (err) {
      console.error('[useAccessRequestListener] Error declining invitation:', err);
      setError(err.message);
      throw err;
    }
  };

  return {
    pendingInvitation,
    acceptInvitation,
    declineInvitation,
    loading,
    error
  };
};
